import { useEffect, useState } from 'react'

import { LoadingMessage } from '@sparkai/auth/src/components/LoadingMessage'
import { Button, Icon, RadialIcon } from '@sparkai/ui'

type ShareLinkAccessStateProps = {
  message?: string
  onRetry?: () => void
  state: 'loading' | 'no-access' | 'not-found' | 'unsupported-role' | 'error'
}

const stateTitle = {
  loading: 'Loading shared link...',
  'no-access': 'You do not have access to this shared link',
  'not-found': 'Shared link not found',
  'unsupported-role': 'This shared link is not available for your role',
  error: 'Unable to open shared link',
}

const loadingMessage =
  'This is taking longer than expected. You can keep waiting, go to your home page, or sign out and try again.'

export function ShareLinkAccessState({ message, onRetry, state }: Readonly<ShareLinkAccessStateProps>) {
  const [hasLoadingTimedOut, setHasLoadingTimedOut] = useState(false)
  const showNextStep = state === 'no-access' || state === 'unsupported-role'
  const isTimedOutLoading = state === 'loading' && hasLoadingTimedOut
  const resolvedState = isTimedOutLoading ? 'error' : state
  const goHome = () => {
    globalThis.location.href = `${globalThis.location.origin}/redirect`
  }

  useEffect(() => {
    if (state !== 'loading') return

    const timeout = globalThis.setTimeout(() => {
      setHasLoadingTimedOut(true)
    }, 8000)

    return () => globalThis.clearTimeout(timeout)
  }, [state])

  if (state === 'loading' && !hasLoadingTimedOut) return <LoadingMessage />

  return (
    <div className='flex h-screen flex-col'>
      <div className='flex h-12 items-center border-b border-b-gray-200 px-4 py-5'>
        <Icon name='logo-colorful' className='ml-2' />
      </div>
      <div className='flex w-full flex-1 flex-col items-center justify-center bg-gray-50 px-6 text-center'>
        <div className='flex max-w-2xl flex-col items-center justify-center gap-6'>
          <RadialIcon variant={resolvedState === 'error' ? 'red' : 'gray'} icon='close-colorful-circle' />
          <h1 className='font-inter text-2xl font-medium text-gray-700'>{stateTitle[resolvedState]}</h1>
          {(message || isTimedOutLoading) && (
            <p className='max-w-2xl text-base font-normal text-gray-500'>
              {isTimedOutLoading ? loadingMessage : message}
            </p>
          )}
          {showNextStep && (
            <p className='max-w-2xl text-base font-normal text-gray-500'>
              Request access or contact your admin for help.
            </p>
          )}
          <div className='flex gap-3'>
            {onRetry && (
              <Button variant='white' className='flex gap-3' onClick={onRetry}>
                <p className='text-sm font-medium'>Retry</p>
              </Button>
            )}
            <Button variant='white' className='flex gap-3' onClick={goHome}>
              <p className='text-sm font-medium'>Back to Home</p>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
